import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { 
  Sparkles, 
  Car, 
  Clock, 
  MapPin, 
  Mail,
  ChevronRight,
  Star,
  Shield,
  Zap,
  Crown,
  TrendingUp,
  Menu,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import MobilWashLogo from "@/components/MobilWashLogo";
import { MobileBookBar } from "@/components/MobileBookBar";
import { AreaLinks } from "@/components/AreaLinks";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Serve card-sized images (the stored URLs point at 1920px / @2x originals)
const optimizeImageUrl = (url) => {
  if (!url) return url;
  try {
    const u = new URL(url);
    if (u.hostname.includes("unsplash.com")) { u.searchParams.set("w", "800"); u.searchParams.set("q", "70"); u.searchParams.set("auto", "format"); }
    if (u.hostname.includes("pexels.com")) { u.searchParams.set("w", "800"); u.searchParams.set("h", "450"); u.searchParams.set("dpr", "1"); }
    return u.toString();
  } catch { return url; }
};

const NAV_LINKS = [["#services", "Services"], ["#pricing", "Pricing"], ["#reviews", "Reviews"], ["#contact", "Contact"]];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal as="nav" onMount y={-100} duration={0.5} className="sticky top-0 left-0 right-0 z-50 glass">
      <div className="container mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <MobilWashLogo className="h-12 md:h-16" />
        </Link>
        <div className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map(([href, label]) => (
            <a key={href} href={href} className="text-gray-200 hover:text-teal-500 transition-colors font-body">{label}</a>
          ))}
          <Link to="/account" data-testid="nav-account-link" className="text-gray-300 hover:text-teal-500 transition-colors font-body">Sign in</Link>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/booking">
            <Button
              data-testid="nav-book-now-btn"
              className="bg-gradient-to-r from-teal-500 to-teal-500 text-white font-bold uppercase tracking-widest hover:shadow-[0_0_25px_rgba(0,240,255,0.6)] transition-all active:scale-95 px-3 md:px-4 text-xs md:text-sm"
            >
              Book Now
            </Button>
          </Link>
          <button type="button" aria-label={open ? "Close menu" : "Open menu"} data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen(!open)} className="md:hidden w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center text-gray-200 hover:text-teal-400">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#0A192F]/95 backdrop-blur-xl px-4 py-3 flex flex-col" data-testid="nav-mobile-menu">
          {NAV_LINKS.map(([href, label]) => (
            <a key={href} href={href} onClick={() => setOpen(false)} className="py-3 text-gray-200 font-body border-b border-white/5 last:border-0">{label}</a>
          ))}
          <Link to="/account" onClick={() => setOpen(false)} data-testid="nav-mobile-account-link" className="py-3 text-gray-300 font-body">Sign in / My bookings</Link>
        </div>
      )}
    </Reveal>
  );
};

const HERO_SLIDES = [
  {
    id: "hero-headline",
    eyebrow: "Mobile Car Valeting",
    headline: (
      <>
        <span className="text-white">Premium</span>
        <br />
        <span className="text-glow-strong text-teal-500">Car Valeting &amp; Detailing</span>
        <br />
        <span className="text-white">At Your Door</span>
      </>
    ),
    subtitle:
      "Professional mobile car valeting services brought directly to your location. Experience showroom quality results without leaving your home.",
  },
  {
    id: "hero-promo",
    eyebrow: "Limited-Time Offer",
    headline: (
      <>
        <span className="text-white">Give Your Car The</span>
        <br />
        <span className="text-glow-strong text-teal-500">Showroom Shine</span>
        <br />
        <span className="text-white">It Deserves</span>
      </>
    ),
    subtitle:
      "Winter Protection offer: book a Mini or Full Valet in November and get Ceramic Wax FREE on every car — or take 25% off Deep Clean Exterior. We come straight to your driveway.",
  },
  {
    id: "hero-same-day",
    eyebrow: "Same-Day Slots",
    headline: (
      <>
        <span className="text-white">Same-Day Valets</span>
        <br />
        <span className="text-glow-strong text-teal-500">Across Glasgow</span>
        <br />
        <span className="text-white">Book Online In 2 Minutes</span>
      </>
    ),
    subtitle:
      "Glasgow, Paisley, Falkirk, West Lothian and Lanarkshire covered. Choose your slot, pay online and we'll be there — with our own water and power.",
  },
  {
    id: "hero-trust",
    eyebrow: "Fully Insured",
    headline: (
      <>
        <span className="text-white">Trusted By</span>
        <br />
        <span className="text-glow-strong text-teal-500">Glasgow Drivers</span>
        <br />
        <span className="text-white">Since Day One</span>
      </>
    ),
    subtitle:
      "Fully insured, professionally trained technicians. Free rescheduling up to 24 hours before your slot.",
  },
  {
    id: "hero-smart-pricing",
    eyebrow: "Smart Pricing",
    headline: (
      <>
        <span className="text-white">Check Every Time</span>
        <br />
        <span className="text-glow-strong text-teal-500">For Discounted Slots</span>
        <br />
        <span className="text-white">&amp; Better Prices</span>
      </>
    ),
    subtitle:
      "Our prices change by day and time — check the booking calendar to find a slot that saves you money automatically at checkout.",
  },
];

const HERO_AUTOPLAY_MS = 6000;

const HeroSlideCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const timerRef = useRef(null);
  const touchStartRef = useRef(null);
  const total = HERO_SLIDES.length;

  const goTo = (i) => setIndex(((i % total) + total) % total);
  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(next, HERO_AUTOPLAY_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, next]);

  // Swipe gestures — works for touch AND pointer/mouse drag
  const handleStart = (clientX) => {
    touchStartRef.current = { x: clientX, y: null };
    setPaused(true);
    setDragOffset(0);
  };
  const handleMove = (clientX) => {
    if (touchStartRef.current == null) return;
    setDragOffset(clientX - touchStartRef.current.x);
  };
  const handleEnd = () => {
    if (touchStartRef.current == null) return;
    const dx = dragOffset;
    touchStartRef.current = null;
    setDragOffset(0);
    setPaused(false);
    if (Math.abs(dx) < 40) return; // ignore taps / tiny drags
    if (dx < 0) next();
    else prev();
  };

  return (
    <div
      className="relative select-none"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => { setPaused(false); handleEnd(); }}
      onTouchStart={(e) => handleStart(e.touches[0].clientX)}
      onTouchMove={(e) => handleMove(e.touches[0].clientX)}
      onTouchEnd={handleEnd}
      onPointerDown={(e) => { if (e.pointerType === "mouse") handleStart(e.clientX); }}
      onPointerMove={(e) => { if (e.pointerType === "mouse" && touchStartRef.current) handleMove(e.clientX); }}
      onPointerUp={(e) => { if (e.pointerType === "mouse") handleEnd(); }}
      data-testid="hero-carousel"
    >
      {/* Eyebrow (also rotates) */}
      <Reveal onMount y={30} className="mb-6 flex justify-center">
        <div className="relative h-8 min-w-[220px] flex items-center justify-center">
          {HERO_SLIDES.map((s, i) => (
            <span
              key={s.id + "-eyebrow"}
              className={`absolute inset-0 flex items-center justify-center px-4 py-2 rounded-full glass text-teal-500 text-sm font-accent uppercase tracking-widest transition-all duration-500 whitespace-nowrap ${
                i === index ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"
              }`}
              aria-hidden={i !== index}
            >
              {s.eyebrow}
            </span>
          ))}
        </div>
      </Reveal>

      {/* Headline + subtitle — first slide's headline is the always-in-DOM H1 for SEO */}
      <div
        className="relative touch-pan-y"
        style={{ transform: `translateX(${dragOffset * 0.35}px)`, transition: dragOffset === 0 ? "transform 0.4s ease" : "none" }}
      >
        {HERO_SLIDES.map((s, i) => {
          const active = i === index;
          const Headline = i === 0 ? "h1" : "div";
          const isFirst = i === 0;
          return (
            <div
              key={s.id}
              data-testid={`hero-slide-${i}`}
              aria-hidden={!active}
              className={`${isFirst ? "relative" : "absolute inset-0"} transition-all duration-700 ${
                active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
              }`}
            >
              <Headline
                className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight mb-6"
              >
                {s.headline}
              </Headline>
              <p className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto mb-2 font-body">
                {s.subtitle}
              </p>
            </div>
          );
        })}
      </div>

      {/* Slide indicator dots */}
      <div className="mt-8 flex items-center justify-center gap-2" role="tablist" aria-label="Hero slides">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.id + "-dot"}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Slide ${i + 1}: ${s.eyebrow}`}
            onClick={() => goTo(i)}
            data-testid={`hero-dot-${i}`}
            className={`h-2 rounded-full transition-all ${
              i === index ? "bg-teal-400 w-8" : "bg-white/20 w-2 hover:bg-white/40"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const HeroSection = () => (
  <section className="hero-bg min-h-screen flex items-center justify-center relative overflow-hidden cyber-grid">
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A192F]/50 to-white/0" />

    <div className="container mx-auto px-6 text-center relative z-10 pt-20">
      <HeroSlideCarousel />

      <Reveal onMount y={30} delay={0.1} className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/booking">
          <Button
            data-testid="hero-book-btn"
            size="lg"
            className="bg-gradient-to-r from-teal-500 to-teal-500 text-white font-bold uppercase tracking-widest text-lg px-8 py-6 hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all active:scale-95"
          >
            Book Your Valet
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </Link>
        <a href="#services">
          <Button
            data-testid="hero-services-btn"
            size="lg"
            variant="outline"
            className="border-teal-500/30 text-teal-500 hover:border-teal-500 hover:bg-cyan-950/30 uppercase tracking-widest text-lg px-8 py-6 transition-all"
          >
            View Services
          </Button>
        </a>
      </Reveal>

      <Reveal onMount y={30} delay={0.2} className="mt-16 grid grid-cols-3 gap-8 max-w-lg mx-auto">
        {[
          { icon: Shield, label: "Insured" },
          { icon: Star, label: "5★ Rated" },
          { icon: Zap, label: "Same Day" }
        ].map((item) => (
          <div key={item.label} className="flex flex-col items-center gap-2">
            <item.icon className="w-8 h-8 text-teal-500" />
            <span className="text-gray-300 text-sm font-accent">{item.label}</span>
          </div>
        ))}
      </Reveal>
    </div>
  </section>
);

// Override descriptions for packages
const packageDescriptions = {
  'mini-valet': 'Quick refresh for your vehicle\'s interior and exterior',
  'full-valet': 'Comprehensive clean inside and out',
  'showroom-valet': 'Premium finish to showroom standard',
  'deep-clean-interior': 'Professional interior revive',
  'deep-clean-exterior': 'Complete exterior refresh',
  'soft-top-restoration': 'Deep clean, colour revival and heavy-duty protection for convertible roofs'
};

const ServiceCard = ({ service, index }) => {
  const description = packageDescriptions[service.id] || service.description;
  // Show "From £X" using the LOWEST matrix price so admins only need to keep the
  // size-based pricing matrix in sync (no separate base-price field to remember).
  const matrixPrices = Object.values(service.pricing || {}).filter(v => typeof v === 'number' && v > 0);
  const fromPrice = matrixPrices.length ? Math.min(...matrixPrices) : service.price;
  
  return (
  <Reveal y={30} delay={index * 0.1}>
    <Card 
      data-testid={`service-card-${service.id}`}
      className="service-card bg-gradient-to-b from-white/5 to-white/0 border-teal-500/30 rounded-xl overflow-hidden h-full"
    >
      <div className="aspect-video relative overflow-hidden">
        <img 
          src={optimizeImageUrl(service.image)}
          alt={service.name}
          loading="lazy"
          decoding="async"
          width="640"
          height="360"
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
        <div className="absolute bottom-4 left-4">
          <span className="px-3 py-1 rounded-full glass text-xs font-accent uppercase tracking-widest text-teal-500">
            {{ valet: 'Valet Package', deep_clean: 'Deep Clean', specialist: 'Specialist' }[service.category] || 'Package'}
          </span>
        </div>
      </div>
      <CardContent className="p-6">
        <h3 className="font-heading text-xl font-bold text-white mb-2">{service.name}</h3>
        <p className="text-gray-300 text-sm mb-4 font-body">{description}</p>
        
        <div className="flex items-center gap-2 mb-4 text-gray-500">
          <Clock className="w-4 h-4" />
          <span className="text-sm font-accent">{service.duration}</span>
        </div>
        
        <ul className="space-y-2 mb-6">
          {service.features.slice(0, 4).map((feature) => (
            <li key={feature} className="flex items-center gap-2 text-sm text-gray-200">
              <Sparkles className="w-3 h-3 text-teal-500" />
              {feature}
            </li>
          ))}
        </ul>
        
        <div className="flex items-center justify-between pt-4 border-t border-teal-500/30">
          <div>
            <span className="text-gray-500 text-sm font-accent">From</span>
            <p className="font-heading text-2xl font-bold text-teal-500 text-glow" data-testid={`service-from-price-${service.id}`}>
              £{fromPrice}
            </p>
          </div>
          <Link to={`/${service.id}`}>
            <Button 
              data-testid={`select-${service.id}-btn`}
              className="bg-teal-500/10 border border-teal-500/30 text-teal-500 hover:bg-teal-500/20 hover:border-teal-500"
            >
              Learn more
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  </Reveal>
);
};

const ServicesSection = ({ packages }) => {
  const valetPackages = packages.filter(p => p.category === 'valet');
  const deepCleanPackages = packages.filter(p => p.category === 'deep_clean');
  
  
  return (
    <section id="services" className="py-24 relative">
      <div className="absolute inset-0 cyber-grid opacity-30" />
      <div className="container mx-auto px-6 relative">
        <Reveal className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full glass text-teal-500 text-sm font-accent uppercase tracking-widest mb-4">
            Our Services
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
            Premium <span className="text-teal-500">Packages</span>
          </h2>
          <p className="text-gray-300 max-w-xl mx-auto font-body">
            Choose from our range of professional valeting services
          </p>
        </Reveal>
        
        <div className="mb-16">
          <h3 className="font-heading text-2xl font-bold text-white mb-8 flex items-center gap-3">
            <Car className="w-6 h-6 text-teal-500" />
            Valet Packages
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {valetPackages.map((service, i) => (
              <ServiceCard key={service.id} service={service} index={i} />
            ))}
          </div>
        </div>
        
        <div>
          <h3 className="font-heading text-2xl font-bold text-white mb-8 flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-teal-500" />
            Deep Clean Services
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {deepCleanPackages.map((service, i) => (
              <ServiceCard key={service.id} service={service} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Accent styling per package (Showroom = gold, Deep Clean Interior = emerald, others = teal)
const PACKAGE_ACCENT = {
  'showroom-valet': { text: 'text-yellow-400', border: 'border-yellow-400/30', bg: 'bg-gradient-to-r from-yellow-400/10 to-orange-400/10' },
  'deep-clean-interior': { text: 'text-emerald-400', border: 'border-emerald-400/30', bg: 'bg-gradient-to-r from-emerald-400/10 to-green-500/10' },
};
const accentFor = (packageId) => PACKAGE_ACCENT[packageId] || { text: 'text-teal-500', border: '', bg: 'bg-gradient-to-r from-teal-500/10 to-purple-500/10' };

const PricingSection = ({ packages, carSizes, pricingMatrix }) => {
  // Format car size for display
  const formatCarSize = (size) => {
    return size.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format package name for display
  const formatPackageName = (name) => {
    return name.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get price from matrix
  const getPrice = (packageId, carSizeId) => {
    const price = pricingMatrix[packageId]?.[carSizeId];
    return price ? `£${price}` : '-';
  };

  const packageOrder = ['mini-valet', 'full-valet', 'showroom-valet', 'deep-clean-interior', 'deep-clean-exterior', 'soft-top-restoration'];
  const carSizeOrder = ['small-sedan', 'sedan', 'suv', 'pickup', 'van'];

  return (
    <section id="pricing" className="py-24 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 cyber-grid opacity-20" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal-500/5 rounded-full blur-[150px]" />
      
      <div className="container mx-auto px-4 sm:px-6 relative">
        <Reveal className="text-center mb-12 sm:mb-16">
          <span className="inline-block px-4 py-2 rounded-full glass text-teal-500 text-sm font-accent uppercase tracking-widest mb-4">
            Transparent Pricing
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Our <span className="text-teal-500">Price Matrix</span>
          </h2>
          <p className="text-gray-300 max-w-xl mx-auto font-body text-sm sm:text-base">
            Simple, upfront pricing based on your vehicle size. No hidden fees.
          </p>
        </Reveal>

        {/* Pricing Table - Desktop */}
        <Reveal y={30} delay={0.2} className="hidden lg:block">
          <div className="glass rounded-2xl overflow-hidden border border-white/10">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="pricing-matrix-table">
                <thead>
                  <tr className="bg-gradient-to-r from-teal-500/10 to-purple-500/10">
                    <th className="text-left p-5 font-heading text-white text-lg border-b border-white/10">
                      Package
                    </th>
                    {carSizeOrder.map((sizeId) => (
                      <th key={sizeId} className="p-5 text-center font-heading text-white border-b border-white/10">
                        <div className="flex flex-col items-center gap-1">
                          <Car className="w-5 h-5 text-teal-500" />
                          <span className="text-sm">{formatCarSize(sizeId)}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {packageOrder.map((packageId, idx) => (
                    <tr 
                      key={packageId} 
                      className={`border-b border-white/5 hover:bg-white/5 transition-colors ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}
                    >
                      <td className="p-5">
                        <div className="flex items-center gap-3">
                          {packageId === 'showroom-valet' && (
                            <Crown className="w-5 h-5 text-yellow-400" />
                          )}
                          {packageId === 'deep-clean-interior' && (
                            <TrendingUp className="w-5 h-5 text-emerald-400" />
                          )}
                          <div>
                            <div className="font-heading text-white font-semibold flex items-center gap-2">
                              {formatPackageName(packageId)}
                              {packageId === 'showroom-valet' && (
                                <span className="text-[10px] bg-gradient-to-r from-yellow-400 to-orange-400 text-black px-2 py-0.5 rounded-full font-bold uppercase">
                                  Premium
                                </span>
                              )}
                              {packageId === 'deep-clean-interior' && (
                                <span className="text-[10px] bg-gradient-to-r from-emerald-400 to-green-500 text-black px-2 py-0.5 rounded-full font-bold uppercase">
                                  Best Seller
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      {carSizeOrder.map((sizeId) => (
                        <td key={sizeId} className="p-5 text-center">
                          <span className={`text-xl font-bold ${accentFor(packageId).text}`}>
                            {getPrice(packageId, sizeId)}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>

        {/* Pricing Cards - Mobile/Tablet */}
        <div className="lg:hidden space-y-6">
          {packageOrder.map((packageId, idx) => (
            <Reveal key={packageId} delay={idx * 0.1}>
              <Card className={`glass border-white/10 overflow-hidden ${accentFor(packageId).border}`}>
                <div className={`p-4 ${accentFor(packageId).bg}`}>
                  <div className="flex items-center gap-3">
                    {packageId === 'showroom-valet' && <Crown className="w-5 h-5 text-yellow-400" />}
                    {packageId === 'deep-clean-interior' && <TrendingUp className="w-5 h-5 text-emerald-400" />}
                    <h3 className="font-heading text-lg font-bold text-white">
                      {formatPackageName(packageId)}
                    </h3>
                    {packageId === 'showroom-valet' && (
                      <span className="text-[10px] bg-gradient-to-r from-yellow-400 to-orange-400 text-black px-2 py-0.5 rounded-full font-bold uppercase">
                        Premium
                      </span>
                    )}
                    {packageId === 'deep-clean-interior' && (
                      <span className="text-[10px] bg-gradient-to-r from-emerald-400 to-green-500 text-black px-2 py-0.5 rounded-full font-bold uppercase">
                        Best Seller
                      </span>
                    )}
                  </div>
                </div>
                <CardContent className="p-4">
                  {/* Price Grid */}
                  <div className="grid grid-cols-5 gap-2">
                    {carSizeOrder.map((sizeId) => (
                      <div key={sizeId} className="text-center">
                        <div className="text-[10px] text-gray-500 mb-1 truncate">
                          {formatCarSize(sizeId).split(' ')[0]}
                        </div>
                        <div className={`text-sm font-bold ${accentFor(packageId).text}`}>
                          {getPrice(packageId, sizeId)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          ))}
        </div>

        {/* CTA */}
        <Reveal className="mt-12 text-center">
          <p className="text-gray-400 text-sm mb-3">
            Add-ons available during booking • Glasgow, Paisley, Falkirk, Stirling, West Lothian, Lanarkshire &amp; parts of Fife
          </p>
          <p className="text-gray-500 text-xs mb-6 max-w-lg mx-auto italic">
            Final prices may vary depending on the condition of your vehicle. Your valeter will assess and inform you of any adjustments before starting the service.
          </p>
          <Link to="/booking">
            <Button 
              data-testid="pricing-book-btn"
              size="lg"
              className="bg-gradient-to-r from-teal-500 to-teal-500 text-white font-bold uppercase tracking-widest hover:shadow-[0_0_25px_rgba(0,240,255,0.6)] transition-all active:scale-95"
            >
              Book Your Valet
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const FALLBACK_TESTIMONIALS = [
  {
    id: "seed-1",
    customer_name: "Sarah M.",
    area: "Glasgow West End",
    rating: 5,
    comment: "Absolutely brilliant service! They came right to my driveway and my car looks better than the day I bought it. The Full Valet is worth every penny.",
    package_name: "Full Valet",
    title: "",
  },
  {
    id: "seed-2",
    customer_name: "James K.",
    area: "Bearsden",
    rating: 5,
    comment: "Booked the Showroom Valet before selling my car — it genuinely helped me get a better price. Professional, punctual and the booking system was so easy to use.",
    package_name: "Showroom Valet",
    title: "",
  },
  {
    id: "seed-3",
    customer_name: "Aisha R.",
    area: "Shawlands",
    rating: 5,
    comment: "Had the Deep Clean Interior after a messy summer with the kids. The seats and carpets came up like new. Will definitely be booking again!",
    package_name: "Deep Clean Interior",
    title: "",
  }
];

const TestimonialCard = ({ review, index }) => (
  <Reveal y={30} duration={0.5} delay={index * 0.15}>
    <Card
      data-testid={`testimonial-card-${index}`}
      className="glass border-white/10 hover:border-teal-500/40 transition-all h-full"
    >
      <CardContent className="p-8 flex flex-col h-full">
        <div className="flex items-center gap-1 mb-4" data-testid={`testimonial-stars-${index}`}>
          {[...Array(review.rating)].map((_, i) => (
            <Star key={`hero-star-${i}`} className="w-5 h-5 text-teal-500 fill-teal-500" />
          ))}
        </div>
        {review.title && (
          <p className="font-heading text-lg font-bold text-white mb-2">{review.title}</p>
        )}
        <p className="text-gray-300 font-body mb-6 flex-grow leading-relaxed">
          &ldquo;{review.comment}&rdquo;
        </p>
        <div className="flex items-center justify-between pt-4 border-t border-white/5">
          <div>
            <p className="text-white font-bold font-body">{review.customer_name}</p>
            <p className="text-gray-500 text-sm font-body">{review.area}</p>
          </div>
          <span className="text-xs text-teal-500 font-accent uppercase tracking-wider px-3 py-1 rounded-full glass">
            {review.package_name}
          </span>
        </div>
      </CardContent>
    </Card>
  </Reveal>
);

const TestimonialsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [aggregate, setAggregate] = useState({ average: 5.0, count: 0 });
  const [isSeeded, setIsSeeded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews?limit=9`)
      .then((r) => {
        if (r.data && r.data.length > 0) {
          setReviews(r.data);
          setIsSeeded(false);
        } else {
          setReviews(FALLBACK_TESTIMONIALS);
          setIsSeeded(true);
        }
      })
      .catch(() => {
        setReviews(FALLBACK_TESTIMONIALS);
        setIsSeeded(true);
      });
    axios.get(`${API}/reviews/aggregate`)
      .then((r) => { if (r.data && r.data.count > 0) setAggregate(r.data); })
      .catch(() => {});
  }, []);

  // Inject Review JSON-LD entries for approved reviews (skip if using seeded placeholders)
  useEffect(() => {
    if (isSeeded || reviews.length === 0) return;
    const scripts = reviews.slice(0, 10).map((r) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-review-ld", r.id);
      s.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Review",
        "itemReviewed": { "@type": "AutoWash", "name": "Mobil Wash" },
        "author": { "@type": "Person", "name": r.customer_name || "Anonymous" },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": String(r.rating),
          "bestRating": "5",
          "worstRating": "1",
        },
        "reviewBody": r.comment,
        "datePublished": r.approved_at || r.created_at,
      });
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
  }, [reviews, isSeeded]);

  return (
    <section id="reviews" className="py-24 relative" data-testid="testimonials-section">
      <div className="container mx-auto px-6">
        <Reveal className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full glass text-teal-500 text-sm font-accent uppercase tracking-widest mb-4">
            Customer Reviews
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
            What Glasgow <span className="text-teal-500">Says</span> About Us
          </h2>
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={`cta-star-${i}`} className="w-6 h-6 text-teal-500 fill-teal-500" />
              ))}
            </div>
            <span className="text-white font-bold text-lg ml-2">{aggregate.average.toFixed(1)}</span>
            <span className="text-gray-500 font-body">
              {aggregate.count > 0 ? `from ${aggregate.count} real reviews` : "rated service"}
            </span>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {reviews.map((review, index) => (
            <TestimonialCard key={review.id || index} review={review} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ContactSection = () => (
  <section id="contact" className="py-24 relative">
    <div className="container mx-auto px-6">
      <Reveal className="max-w-2xl mx-auto text-center">
        <span className="inline-block px-4 py-2 rounded-full glass text-teal-500 text-sm font-accent uppercase tracking-widest mb-4">
          Get In Touch
        </span>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
          Ready to <span className="text-teal-500">Transform</span> Your Car?
        </h2>
        <p className="text-gray-300 mb-10 font-body">
          Book your mobile valeting service today and experience the difference
        </p>
        
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-10">
          <a 
            href="mailto:info@mobilwash.co.uk"
            className="flex items-center gap-3 glass px-6 py-4 rounded-xl hover:border-teal-500/50 transition-all"
          >
            <Mail className="w-6 h-6 text-teal-500" />
            <span className="text-white font-body">info@mobilwash.co.uk</span>
          </a>
          <div className="flex items-center gap-3 glass px-6 py-4 rounded-xl">
            <MapPin className="w-6 h-6 text-teal-500" />
            <span className="text-white font-body">Mobile Service - We Come To You</span>
          </div>
        </div>
        
        <Link to="/booking">
          <Button 
            data-testid="contact-book-btn"
            size="lg"
            className="bg-gradient-to-r from-teal-500 to-blue-500 text-black font-bold uppercase tracking-widest text-lg px-10 py-6 hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all active:scale-95"
          >
            Book Now
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </Link>
      </Reveal>
    </div>
  </section>
);

const AboutSection = () => (
  <section id="about" className="py-20 border-t border-white/5" data-testid="about-section">
    <div className="container mx-auto px-6 max-w-4xl">
      <Reveal>
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
          Mobile Car Valeting Across <span className="text-teal-500">Central Scotland</span>
        </h2>
        <div className="space-y-4 text-gray-300 font-body text-base leading-relaxed">
          <p>
            Mobil Wash is a fully insured mobile car valeting company. Our valeters travel to your home, driveway or workplace
            with their own water and power supply, so there is nothing for you to set up — simply hand over the keys and carry on
            with your day while we restore your car inside and out.
          </p>
          <p>
            Our Glasgow team covers every G postcode together with Paisley and Renfrewshire (PA1–PA13). A second team looks after
            Falkirk and Stirling (FK1–FK16), West Lothian including Livingston, Bathgate and Linlithgow (EH27–EH30, EH47–EH54),
            Lanarkshire towns such as Motherwell, Hamilton and Coatbridge (ML1–ML7) and Dunfermline (KY11–KY12). Prices are the
            same wherever we visit and there is never a travel surcharge.
          </p>
          <p>
            Choose from a quick <Link to="/mini-valet" className="text-teal-400 hover:underline">Mini Valet</Link>, a thorough{" "}
            <Link to="/full-valet" className="text-teal-400 hover:underline">Full Valet</Link>, our premium{" "}
            <Link to="/showroom-valet" className="text-teal-400 hover:underline">Showroom Valet</Link>, or a specialist{" "}
            <Link to="/deep-clean-interior" className="text-teal-400 hover:underline">Deep Clean Interior</Link> or{" "}
            <Link to="/deep-clean-exterior" className="text-teal-400 hover:underline">Deep Clean Exterior</Link>. Convertible owners can book our{" "}
            <Link to="/soft-top-restoration" className="text-teal-400 hover:underline">Soft Top Restoration &amp; Protection</Link> package. Add extras like
            High Definition Wax, Ceramic Spray, Iron Remover, Mould Removal, Ozone Treatment or Air-Con Sanitation during booking,
            pick a time slot that suits you and pay securely online or on the day.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const Footer = () => (
  <footer className="py-8 border-t border-white/5">
    <div className="container mx-auto px-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <MobilWashLogo className="h-9" />
        </div>
        <AreaLinks className="max-w-2xl" />
        <p className="text-gray-500 text-sm font-body">
          © {new Date().getFullYear()} Mobil Wash Mobile Car Valeting. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

const HomePage = () => {
  const [packages, setPackages] = useState([]);
  const [carSizes, setCarSizes] = useState([]);
  const [pricingMatrix, setPricingMatrix] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [packagesRes, carSizesRes, pricingRes] = await Promise.all([
          axios.get(`${API}/packages`),
          axios.get(`${API}/car-sizes`),
          axios.get(`${API}/pricing-matrix`)
        ]);
        setPackages(packagesRes.data);
        setCarSizes(carSizesRes.data);
        setPricingMatrix(pricingRes.data);
      } catch (err) {
        // Failed to load data - show empty lists
        setPackages([]);
        setCarSizes([]);
        setPricingMatrix({});
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A192F]">
      <Navbar />
      <HeroSection />
      {loading ? (
        <div className="py-24 flex items-center justify-center" data-testid="home-sections-loading">
          <div className="loading-spinner" />
        </div>
      ) : (
        <>
          <ServicesSection packages={packages} />
          <PricingSection packages={packages} carSizes={carSizes} pricingMatrix={pricingMatrix} />
        </>
      )}
      <AboutSection />
      <TestimonialsSection />
      <ContactSection />
      <Footer />
      <MobileBookBar />
    </div>
  );
};

export default HomePage;
